import { env } from 'next-runtime-env';

import type { BotAvailabilityPaymentOption } from '~entities/bot';
import { getBotsApi } from '~services/api/bots/dynamic';
import { api, apiTags } from '~shared/api/origin';
import {
	eventsInbox,
	EventType,
	isMsgType,
} from '~shared/services/eventsInbox';
import { isSafeApp } from '~shared/utils/host';
import type { RootState } from '~store/index';
import { roundToTwoDigits, sortShopProducts } from '~utils/payments';

import { gridApi } from '../grid';
import type {
	TProductSignature,
	TShop,
	TShopItem,
	TShopItemResponse,
	TShopPromoCodeResponse,
	TUserBalance,
	TUserShopInfo,
} from './types';

const prefix = '/credits';

export const creditsApi = api.injectEndpoints({
	endpoints: (build) => ({
		balance: build.query<TUserBalance, { id: string }>({
			query: ({ id }) => ({
				url: `${prefix}/accounts/${id}/balance`,
			}),
			async onCacheEntryAdded(
				{},
				{ updateCachedData, cacheDataLoaded, cacheEntryRemoved }
			) {
				let unsub = null;
				try {
					await cacheDataLoaded;

					unsub = eventsInbox.onMsg((msg) => {
						if (
							isMsgType(
								msg,
								EventType.BalanceChanged,
								EventType.NeuronsReceived
							)
						) {
							updateCachedData((draft) => {
								draft.balance = msg.payload.balance;
							});
						}
					});
				} catch (err) {}

				await cacheEntryRemoved;
				unsub?.();
			},
		}),
		shop: build.query<TShop, void>({
			async queryFn(_, _queryApi, _extraOptions, fetchWithBQ) {
				const {
					auth: {
						info: { id },
					},
				} = _queryApi.getState() as RootState;

				const [
					{ data: userShopData, error: userShopDataError },
					productsResponse,
				] = await Promise.all([
					fetchWithBQ(`/exo/moto/${id}/details`), // user ip and token for moto form
					fetchWithBQ(`/exo/moto/${id}/credits`),
				]);
				const error = productsResponse.error || userShopDataError;
				if (error) {
					return { error };
				}
				const products = productsResponse.data as {
					packs: Array<TShopItemResponse>;
				};
				const userData: TUserShopInfo = (userShopData as TUserShopInfo) ?? {
					userIp: '',
					token: '',
					country: '',
					oneClickPaymentAvailable: false,
				};
				userData.oneClickPaymentAvailable = isSafeApp
					? false
					: userData.oneClickPaymentAvailable;

				const convertedProducts: Array<TShopItem> = products.packs.map((e) => ({
					...e,
					price: roundToTwoDigits(e.price.gross),
				}));

				return {
					data: sortShopProducts(convertedProducts, userData),
				};
			},
			serializeQueryArgs: ({ endpointName }) => {
				return `${endpointName}`;
			},
			providesTags: [apiTags.shop],
		}),
		oneClickBuy: build.mutation<
			{
				paymentId: string;
			},
			{
				userId: string;
				sku: string;
				PromoCode?: string;
				country: string;
				shownInfo: {
					price: number;
					currency: string;
				};
			}
		>({
			query: ({ userId, ...body }) => ({
				url: `/exo/moto/${userId}/payments/oneclick`,
				method: 'POST',
				body: {
					...body,
					merchantId: env('NEXT_PUBLIC_MOTO_MERCHANT_ID') ?? '',
				},
			}),
		}),
		applyPromoCode: build.mutation<
			TShopPromoCodeResponse,
			{ PromoCode: string; skipUsageCheck?: true; skipSubscriptionCheck?: true }
		>({
			async queryFn(
				{ PromoCode, skipUsageCheck, skipSubscriptionCheck },
				_queryApi,
				_extraOptions,
				fetchWithBQ
			) {
				const {
					auth: {
						info: { id },
					},
				} = _queryApi.getState() as RootState;

				const resp = await fetchWithBQ({
					url: `/ai/benefits/${id}/promocode/validate`,
					method: 'POST',
					body: {
						PromoCode,
						skipUsageCheck,
						skipSubscriptionCheck,
					},
				});

				const error = resp.error;
				if (error) {
					return { error };
				}

				const data = resp.data as TShopPromoCodeResponse;
				data.Prices = data.Prices.filter((e) => e.PromoPrice !== e.Price).map(
					(e) => ({
						...e,
						PromoPrice: roundToTwoDigits(e.PromoPriceDecomposition.Gross),
					})
				);
				return {
					data: { ...data, PromoCode },
				};
			},
		}),
		buyBot: build.mutation<
			undefined,
			{
				id: string;
				botId: string | number;
				type: BotAvailabilityPaymentOption['type'];
			}
		>({
			query: ({ id, botId, type }) => ({
				url: `/ai/chats/userId/${id}/botId/${botId}/buy`,
				method: 'POST',
				body: {
					type,
				},
			}),
			// TODO: Maybe invalidesTags ... ?
			async onQueryStarted(
				{ botId, id },
				{ dispatch, queryFulfilled, getState }
			) {
				const botsApi = await getBotsApi();
				try {
					await queryFulfilled;

					dispatch(
						botsApi.util.updateQueryData(
							'getBotProfile',
							{ userId: id, recipientId: botId },
							(draft) => {
								if (draft?.conditions) {
									draft.conditions.preCondition = undefined;
								}
							}
						)
					);
					dispatch(
						botsApi.util.updateQueryData(
							'getNextBots',
							{ userId: id },
							(draft) => {
								if (draft?.bots.length) {
									draft.bots = draft.bots.filter(
										(bot) => +bot.clientId !== +botId
									);
								}
							}
						)
					);
					dispatch(
						botsApi.util.updateQueryData(
							'getBotAccessInfo',
							{ userId: id, recipientId: String(botId) },
							(draft) => {
								if (draft) {
									draft.access = true;
									draft.reason = undefined;
								}
							}
						)
					);
					for (const { originalArgs } of gridApi.util.selectInvalidatedBy(
						getState(),
						[apiTags.grid]
					)) {
						dispatch(
							gridApi.util.updateQueryData('getGrid', originalArgs, (draft) => {
								const bot = draft.botGrid.find(
									({ clientId }) => clientId === +botId
								);
								if (bot) {
									bot.preCondition = undefined;
								}
							})
						);
					}
				} catch {}
			},
		}),
		paymentSignature: build.query<
			TProductSignature,
			{ userId: string; sku: string; promoCode?: string }
		>({
			query: ({ userId, sku, promoCode }) => ({
				url: `/exo/moto/${userId}/payments`,
				method: 'POST',
				body: {
					sku,
					promoCode,
					merchantId: env('NEXT_PUBLIC_MOTO_MERCHANT_ID') ?? '',
				},
			}),
		}),
	}),
});

export const {
	useBalanceQuery,
	useShopQuery,
	useBuyBotMutation,
	useOneClickBuyMutation,
	useApplyPromoCodeMutation,
	usePaymentSignatureQuery,
} = creditsApi;
